import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingBag, ShoppingCart, Loader2, Plus, Minus, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);

export default function Storefront() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [cart, setCart] = useState({}); // { catalogId: qty }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/store/${slug}`).then(({ data }) => setData(data)).catch(() => setErr(true));
    const p = new URLSearchParams(window.location.search);
    if (p.get("success")) {
      toast.success("Merci pour votre achat ! Un email de confirmation vous a été envoyé.");
      window.history.replaceState({}, "", `/boutique/${slug}`);
    }
  }, [slug]);

  const add = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart((c) => { const q = (c[id] || 0) - 1; const n = { ...c }; if (q <= 0) delete n[id]; else n[id] = q; return n; });
  const removeLine = (id) => setCart((c) => { const n = { ...c }; delete n[id]; return n; });

  const items = data?.items || [];
  const lines = useMemo(() => Object.entries(cart)
    .map(([id, qty]) => { const it = items.find((x) => x.id === id); return it ? { ...it, qty } : null; })
    .filter(Boolean), [cart, items]);
  const total = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const count = lines.reduce((s, l) => s + l.qty, 0);

  const checkout = async () => {
    setBusy(true);
    try {
      const payload = { items: lines.map((l) => ({ catalog_id: l.id, qty: l.qty })), origin_url: window.location.origin };
      const { data } = await axios.post(`${API}/store/${slug}/checkout`, payload);
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); setBusy(false); }
  };

  if (err) return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 text-zinc-900">
      <div className="text-center"><ShoppingBag className="mx-auto h-10 w-10 text-zinc-300" /><p className="mt-3 text-zinc-500">Cette boutique n'existe pas ou n'est pas encore disponible.</p></div>
    </div>
  );
  if (!data) return (<div className="flex min-h-screen items-center justify-center bg-zinc-50"><Loader2 className="h-8 w-8 animate-spin text-zinc-400" /></div>);

  const color = data.org.brand_color || "#FF6A00";
  return (
    <div className="min-h-screen bg-zinc-50 pb-16 text-zinc-900" data-testid="storefront-page">
      <header className="px-6 py-8 text-white" style={{ background: color }}>
        <div className="mx-auto flex max-w-5xl items-center gap-4">
          {data.org.logo ? <img src={data.org.logo} alt={data.org.name} className="h-12 w-12 rounded-xl bg-white/20 object-contain p-1" />
            : <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20"><ShoppingBag className="h-6 w-6" /></div>}
          <div><h1 className="text-2xl font-bold">{data.org.name}</h1>{data.org.tagline && <p className="text-sm text-white/85">{data.org.tagline}</p>}</div>
        </div>
      </header>

      <main className="mx-auto grid max-w-5xl gap-8 px-6 py-10 lg:grid-cols-[1fr_320px]">
        <div>
          <h2 className="mb-1 text-lg font-semibold">Boutique en ligne</h2>
          <p className="mb-6 text-sm text-zinc-500">Ajoutez des articles à votre panier et réglez en une fois. Paiement sécurisé par Stripe.</p>
          {items.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-zinc-300 py-16 text-center text-zinc-400">Aucun article disponible pour le moment.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {items.map((it) => (
                <div key={it.id} data-testid={`store-item-${it.id}`} className="flex flex-col rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                  <h3 className="font-semibold">{it.name}</h3>
                  {it.description && <p className="mt-1 flex-1 text-sm text-zinc-500">{it.description}</p>}
                  <p className="mt-3 text-2xl font-bold" style={{ color }}>{eur(it.price)}<span className="text-xs font-normal text-zinc-400"> / {it.unit}</span></p>
                  {cart[it.id] ? (
                    <div className="mt-4 flex items-center gap-3">
                      <button onClick={() => dec(it.id)} data-testid={`store-dec-${it.id}`} className="flex h-8 w-8 items-center justify-center rounded-full border border-zinc-300 text-zinc-700 hover:bg-zinc-100"><Minus className="h-4 w-4" /></button>
                      <span className="min-w-6 text-center font-semibold" data-testid={`store-qty-${it.id}`}>{cart[it.id]}</span>
                      <button onClick={() => add(it.id)} data-testid={`store-inc-${it.id}`} className="flex h-8 w-8 items-center justify-center rounded-full border border-zinc-300 text-zinc-700 hover:bg-zinc-100"><Plus className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <button data-testid={`store-add-${it.id}`} onClick={() => add(it.id)} className="mt-4 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90" style={{ background: color }}><Plus className="h-4 w-4" />Ajouter au panier</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <aside data-testid="store-cart" className="h-fit rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm lg:sticky lg:top-6">
          <div className="mb-3 flex items-center gap-2 font-semibold">
            <ShoppingCart className="h-5 w-5" style={{ color }} />Panier
            {count > 0 && <span className="ml-auto rounded-full px-2 py-0.5 text-xs text-white" style={{ background: color }}>{count}</span>}
          </div>
          {lines.length === 0 ? (
            <p className="py-6 text-center text-sm text-zinc-400">Votre panier est vide.</p>
          ) : (
            <>
              <div className="space-y-3">
                {lines.map((l) => (
                  <div key={l.id} className="flex items-start gap-2 text-sm">
                    <div className="flex-1"><p className="font-medium">{l.name}</p><p className="text-zinc-500">{l.qty} × {eur(l.price)}</p></div>
                    <span className="font-semibold">{eur(l.price * l.qty)}</span>
                    <button onClick={() => removeLine(l.id)} className="text-zinc-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-zinc-200 pt-3 font-semibold">Total<span data-testid="store-cart-total">{eur(total)}</span></div>
              <button data-testid="store-checkout-btn" onClick={checkout} disabled={busy} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60" style={{ background: color }}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}Payer {eur(total)}
              </button>
            </>
          )}
        </aside>
      </main>
      <p className="text-center text-xs text-zinc-400">Propulsé par HalgarOS · Paiement sécurisé par Stripe</p>
    </div>
  );
}
